"use client";
import { Button } from "@cmp-src/primitives/button";
import { Dialog, DialogContent, DialogTitle } from "@cmp-src/primitives/Dialog";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import {
  MAX_COMMENT_ATTACHMENTS,
  WorkplaceRequestElement,
  WorkplaceRequestEventUser
} from "@gettactic/api";
import { DeleteConfirmModal } from "@gettactic/components/src/overlay/modals/DeleteConfirmModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@gettactic/components/src/primitives/dropdown-menu";
import { FileDropZone } from "@gettactic/components/src/upload/FileDropZone";
import { cn } from "@gettactic/helpers";
import { formatFileSize } from "@gettactic/helpers/src/files/fileType";
import { Archive, MoreHorizontal, Share2, Trash2 } from "lucide-react";
import { type ReactNode } from "react";
import { ApproveRequestModal } from "@/features/workplace-requests/components/ApproveRequestModal/ApproveRequestModal";
import { DeskAutomationPanel } from "@/features/workplace-requests/components/DeskAutomationPanel";
import { DetailSidebarNavBar } from "@/features/workplace-requests/components/detail-modal/DetailSidebarNavBar";
import { LinkedRequestsSection } from "@/features/workplace-requests/components/detail-modal/LinkedRequestsSection";
import { useCondensedHeader } from "@/features/workplace-requests/components/detail-modal/useCondensedHeader";
import { RequestApprovalPanel } from "@/features/workplace-requests/components/RequestApprovalPanel";
import { MAX_ATTACHMENT_BYTES } from "@/features/workplace-requests/hooks/usePendingAttachments";
import { DescriptionSection } from "../RequestDetailsModal/DescriptionSection";
import { EditRequestModal } from "../RequestDetailsModal/EditRequestModal";
import { RequestCommentComposer } from "../RequestDetailsModal/RequestCommentComposer";
import { RequestFieldValues } from "../RequestDetailsModal/RequestFieldValues";
import { ShareRequestModal } from "../RequestDetailsModal/ShareRequestModal";
import { Sidebar } from "../RequestDetailsModal/Sidebar";
import { CommentsActivitySection } from "./CommentsActivitySection";
import {
  RequestDetailHeaderBar,
  RequestDetailHeaderDetails
} from "./RequestDetailHeader";
import { RequestDetailsSkeleton } from "./RequestDetailsSkeleton";
import { RequestLocationSection } from "./RequestLocationSection";
import { isValidWorkplaceRequest } from "./request-guards";
import { RequestDetailsPageProps } from "./types";
import { useRequestDetailController } from "./useRequestDetailController";

export function RequestDetailsPage({
  dontTouchUrl = false,
  requestId,
  isAdmin = false,
  onClose,
  onError,
  isPageMode = false,
  initialRequest = null,
  onRequestUpdate,
  isInMobileDrawer = false,
  schedule = null,
  contained = false,
  portalContainer,
  navIndex,
  navTotal,
  onNavigatePrev,
  onNavigateNext
}: RequestDetailsPageProps) {
  const {
    request,
    isRequestLoading,
    authenticatedUser,
    comments,
    commentsLoading,
    eventsData,
    eventsLoading,
    workplaceRequestTypes,
    categories,
    createComment,
    updateComment,
    updateWorkplaceRequestMutation,
    respondToApprovalMutation,
    reservationWorkflows,
    followUps,
    skippedWorkflowIds,
    releasedDeskOverrides,
    isLoadingReservationWorkflows,
    isViewingLinkedRequest,
    newComment,
    setNewComment,
    commentAttachments,
    isEditingDescription,
    editedDescription,
    setEditedDescription,
    deleteConfirm,
    editModal,
    shareModal,
    reservationModal,
    reservationApprovalError,
    activityTab,
    setActivityTab,
    deleteCommentConfirm,
    editingCommentId,
    editingCommentText,
    setEditingCommentText,
    pendingChanges,
    handleSubmitComment,
    handleEditComment,
    handleDeleteComment,
    handleStartEditComment,
    handleCancelEditComment,
    handleConfirmDeleteComment,
    handleStatusChange,
    handleApprovalResponse,
    handleApproveWithSelection,
    handleToggleFollowUp,
    handleReleasedDeskChange,
    handleCloseReservationModal,
    handleLinkedStatusChange,
    handleOpenLinkedRequest,
    handleOwnerChange,
    handleTypeChange,
    handlePriorityChange,
    handleLocationChange,
    handleSaveDescription,
    handleCancelEdit,
    handleStartEdit,
    handleDeleteRequest,
    handleArchive,
    handleModalClose
  } = useRequestDetailController({
    requestId,
    isPageMode,
    dontTouchUrl,
    initialRequest,
    onError,
    onClose,
    onRequestUpdate
  });

  const { setViewportEl, isCondensed: isHeaderCondensed } =
    useCondensedHeader();

  // Show skeleton when loading in page mode
  if (isPageMode && (isRequestLoading || !request)) {
    return <RequestDetailsSkeleton />;
  }

  // Don't render anything if no request and not in page mode (modal mode)
  if (!request && !isPageMode) return null;

  // If we have a request but it's not valid, show error in page mode
  if (isPageMode && !isValidWorkplaceRequest(request)) {
    onError?.("Invalid request data");
    return <RequestDetailsSkeleton />;
  }

  // At this point, we know request is valid if we're rendering
  const validRequest = request as WorkplaceRequestElement;
  const TitleTag = isPageMode ? H1 : DialogTitle;
  const selectedType = workplaceRequestTypes.find(
    (t) => t.id === validRequest.type.id
  );
  const category = selectedType
    ? categories.allElements.find((c) => c.id === selectedType.category_id)
    : undefined;
  // Tints the header icon tile to match the category (as the queue table does).
  const categoryColor = category?.color ?? undefined;
  const officeName =
    authenticatedUser.offices?.byId?.[validRequest.office_id]?.name ||
    validRequest.office_id;
  const submitterName = validRequest.submitter_user?.name || "Unknown";
  const events =
    eventsData?.pages?.flatMap((page) => page?.elements || []) || [];
  const eventUsers =
    eventsData?.pages?.reduce(
      (acc, page) => (page?.users ? Object.assign(acc, page.users) : acc),
      {} as Record<string, WorkplaceRequestEventUser>
    ) || {};

  // The reservation an opener hands in belongs to the request it opened, not to
  // a linked one the reader followed: without this the sidebar would offer the
  // parent's reservation to edit from a follow-up, and the location section —
  // which skips its own fetch whenever a schedule is passed — would draw the
  // parent's rooms. Dropped, each linked request resolves its own by id.
  const activeSchedule = isViewingLinkedRequest ? null : schedule;

  const shareAction = validRequest.capabilities?.can_share ? (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Share request"
      onClick={() => shareModal.open()}
      className="h-8 w-8 border border-neutral-200 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700"
    >
      <Share2 className="h-4 w-4" />
    </Button>
  ) : null;

  const adminActions = isAdmin ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="More actions"
          className="h-8 w-8 border border-neutral-200 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleArchive}>
          <Archive className="mr-2 h-4 w-4" />
          Archive request
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => deleteConfirm.open()}
          className="text-red-600 focus:text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete request
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  const content = (
    <>
      <div className="flex h-full min-h-0 max-sm:h-auto max-sm:flex-col">
        {/* Main column: header (fixed) + scrollable body + fixed composer.
            The whole column is the drop target, so a file can be dragged onto
            the conversation rather than aimed at the composer. */}
        <FileDropZone
          onDrop={commentAttachments.addFiles}
          hint={`Up to ${MAX_COMMENT_ATTACHMENTS} files, ${formatFileSize(MAX_ATTACHMENT_BYTES)} each`}
          className="flex min-h-0 min-w-0 flex-1 flex-col"
        >
          {/* Fixed top bar (constant height, outside the scroll viewport) */}
          <RequestDetailHeaderBar
            request={validRequest}
            selectedType={selectedType}
            categoryColor={categoryColor}
            isInMobileDrawer={isInMobileDrawer}
            onClose={onClose}
            shareAction={shareAction}
            adminActions={adminActions}
            condensed={isHeaderCondensed}
          />

          {/* Scrollable body — custom (thin) scrollbar via ScrollArea */}
          <ScrollArea
            viewportRef={setViewportEl}
            className="min-h-0 flex-1 max-sm:overflow-visible"
            viewportClassName="px-8 pb-6 max-sm:overflow-visible max-sm:px-4"
            data-modal-content
          >
            {/* Large title + chips — scrolls away; the bar above takes over */}
            <RequestDetailHeaderDetails
              request={validRequest}
              selectedType={selectedType}
              categoryColor={categoryColor}
              officeName={officeName}
              submitterName={submitterName}
              TitleTag={TitleTag}
            />

            {/* The approval decision — and what approving will create — leads
                the request, directly under its title. */}
            <RequestApprovalPanel
              request={validRequest}
              selectedType={selectedType}
              workplaceRequestTypes={workplaceRequestTypes}
              authenticatedUser={authenticatedUser}
              onApprovalResponse={handleApprovalResponse}
              isApprovalLoading={
                respondToApprovalMutation.isPending ||
                isLoadingReservationWorkflows
              }
              onLocationChange={handleLocationChange}
              followUps={followUps}
              skippedWorkflowIds={skippedWorkflowIds}
              onToggleFollowUp={handleToggleFollowUp}
              releasedDeskOverrides={releasedDeskOverrides}
              onReleasedDeskChange={handleReleasedDeskChange}
            />

            {/* Description Section — sits directly under the header, above the
                map. Types that hide the description carry none, so the section
                (including the admin "Add a description" prompt) is dropped. */}
            {selectedType?.hide_description ? null : (
              <DescriptionSection
                request={validRequest}
                showTopDivider={false}
                isEditing={isEditingDescription.state}
                editedDescription={editedDescription}
                onDescriptionChange={setEditedDescription}
                onStartEdit={handleStartEdit}
                onSave={handleSaveDescription}
                onCancel={handleCancelEdit}
                isLoading={updateWorkplaceRequestMutation.isLoading}
              />
            )}

            {/* Location on map — shown when the request has a resource or pin,
                followed by the rooms of the reservation when there are several */}
            <RequestLocationSection
              request={validRequest}
              type={selectedType}
              officeName={officeName}
              schedule={activeSchedule}
            />

            {/* Request details (custom form fields) + Edit */}
            <RequestFieldValues
              request={validRequest}
              fields={selectedType?.fields}
              workflows={selectedType?.workflows}
              onEdit={() => editModal.open()}
            />

            {/* The desk changes approving this request will make, and — once it
                has been approved — how each of them actually went */}
            <DeskAutomationPanel
              request={validRequest}
              type={selectedType}
              releasedDeskOverrides={releasedDeskOverrides}
            />

            {/* The requests this one spawned, plus the follow-ups its approver
                deliberately skipped */}
            <LinkedRequestsSection
              request={validRequest}
              type={selectedType}
              workplaceRequestTypes={workplaceRequestTypes}
              onOpenRequest={handleOpenLinkedRequest}
              onStatusChange={handleLinkedStatusChange}
            />

            {/* Comments / Activity — one section with a toggle (composer in footer) */}
            <CommentsActivitySection
              activityTab={activityTab}
              onActivityTabChange={setActivityTab}
              comments={comments}
              commentsLoading={commentsLoading}
              authenticatedUser={authenticatedUser}
              editingCommentId={editingCommentId}
              editingCommentText={editingCommentText}
              onStartEditComment={handleStartEditComment}
              onCancelEditComment={handleCancelEditComment}
              onEditComment={handleEditComment}
              onDeleteComment={handleConfirmDeleteComment}
              onEditingCommentTextChange={setEditingCommentText}
              updateCommentLoading={updateComment.isLoading}
              events={events}
              eventUsers={eventUsers}
              eventsLoading={eventsLoading}
            />
          </ScrollArea>

          {/* Fixed footer composer */}
          <RequestCommentComposer
            newComment={newComment}
            onCommentChange={setNewComment}
            onSubmit={handleSubmitComment}
            isLoading={createComment.isLoading}
            authenticatedUser={authenticatedUser}
            recipientName={validRequest.submitter_user?.name}
            attachments={commentAttachments.attachments}
            onAddFiles={commentAttachments.addFiles}
            onRemoveAttachment={commentAttachments.remove}
            onRetryAttachment={commentAttachments.retry}
            isUploadingAttachments={commentAttachments.isUploading}
            remainingAttachmentSlots={commentAttachments.remainingSlots}
          />
        </FileDropZone>

        {/* Sidebar column: nav/close bar + scrollable details */}
        <div className="flex min-h-0 w-full flex-col border-neutral-200 border-l bg-[#F6F8FA] max-sm:border-t max-sm:border-l-0 md:w-80">
          {!isInMobileDrawer && (
            <DetailSidebarNavBar
              // The opener's list positions the request it opened, not the
              // linked one being read — so the counter and arrows step aside
              // until the reader is back on it.
              navIndex={isViewingLinkedRequest ? undefined : navIndex}
              navTotal={isViewingLinkedRequest ? undefined : navTotal}
              onNavigatePrev={
                isViewingLinkedRequest ? undefined : onNavigatePrev
              }
              onNavigateNext={
                isViewingLinkedRequest ? undefined : onNavigateNext
              }
              onClose={handleModalClose}
              itemNoun="request"
            />
          )}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-5 pb-6 max-sm:overflow-visible max-sm:px-4">
            <Sidebar
              request={validRequest}
              workplaceRequestTypes={workplaceRequestTypes}
              pendingChanges={pendingChanges}
              onOwnerChange={handleOwnerChange}
              onStatusChange={handleStatusChange}
              onTypeChange={handleTypeChange}
              onPriorityChange={handlePriorityChange}
              onDeleteRequest={() => deleteConfirm.open()}
              onOpenLinkedRequest={handleOpenLinkedRequest}
              schedule={activeSchedule}
            />
          </div>
        </div>
      </div>

      <EditRequestModal
        isOpen={editModal.isOpen}
        onClose={() => editModal.close()}
        request={validRequest}
        type={selectedType}
      />

      <ShareRequestModal
        isOpen={shareModal.isOpen}
        onClose={() => shareModal.close()}
        request={validRequest}
      />

      <ApproveRequestModal
        isOpen={reservationModal.isOpen}
        onClose={handleCloseReservationModal}
        request={validRequest}
        workflows={reservationWorkflows}
        onConfirm={handleApproveWithSelection}
        isSubmitting={respondToApprovalMutation.isPending}
        submitError={reservationApprovalError}
      />
    </>
  );

  // Shared delete confirmations (handle desktop + mobile internally), rendered in
  // every layout branch below.
  const deleteModals = (
    <>
      <DeleteConfirmModal
        isOpen={deleteConfirm.isOpen}
        onOpenChange={deleteConfirm.onOpenChange}
        onConfirm={handleDeleteRequest}
        title="Delete request"
        message={`Are you sure you want to delete "${validRequest.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
      />
      <DeleteConfirmModal
        isOpen={deleteCommentConfirm.isOpen}
        onOpenChange={deleteCommentConfirm.onOpenChange}
        onConfirm={handleDeleteComment}
        title="Delete comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );

  // If in mobile drawer, render content directly without modal wrapper
  if (isInMobileDrawer) {
    return (
      <>
        {content}
        {deleteModals}
      </>
    );
  }

  if (isPageMode) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
        {/* Modal-like container */}
        <div className="relative flex h-[98vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:h-[95vh]">
          {content}
          {deleteModals}
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog
        open={true}
        onOpenChange={() => {
          if (!isPageMode && !dontTouchUrl) {
            window.history.pushState(null, "", "/workplace-requests");
          }
          onClose?.();
        }}
      >
        <DialogContent
          contained={contained}
          portalContainer={portalContainer}
          // Suppress the built-in close button; the sidebar bar owns the close/nav.
          customCloseButton={<span aria-hidden className="hidden" />}
          className={cn(
            "flex flex-col gap-0 overflow-hidden rounded-brand-xl bg-white p-0",
            // Contained: fixed size relative to the container (never grows with
            // content), matching the mockup spec. Override the variant max-w and
            // the `md:w-full` breakpoint so the width caps at 1240px.
            contained
              ? "h-[min(880px,calc(100%-40px))] w-[min(1240px,calc(100%-40px))] max-w-none md:w-[min(1240px,calc(100%-40px))]"
              : "h-[min(880px,90vh)]"
          )}
          style={
            contained
              ? undefined
              : { width: "66rem", minWidth: "32rem", maxWidth: "66rem" }
          }
        >
          {content}
        </DialogContent>
      </Dialog>
      {deleteModals}
    </>
  );
}

const H1 = ({ children }: { children: ReactNode }) => (
  <h1 className="font-mona font-semibold text-2xl text-neutral-900">
    {children}
  </h1>
);
